import {useEffect, useCallback, useRef} from '@wordpress/element';
import apiFetch from '@wordpress/api-fetch';
import {convertPayPalAddressToCart, extractShippingMethod} from "@ppcp/utils";
import {
    extractShippingRateParams,
    getRestPath,
    i18n
} from '../../../utils';
import {useApiRequests} from "../../../hooks";

export const usePayPalOptions = (
    {
        isExpress,
        paypal,
        needsSetupToken,
        buttonStyles,
        shippingData,
        billing,
        setError,
        setPaymentData,
        onClick,
        onClose,
        buttonsContainer
    }) => {
    const currentShippingData = useRef(shippingData);
    const currentBilling = useRef(billing);
    const currentData = useRef({
        onClick,
        onClose,
        buttonState: true,
        actions: {},
        error: null,
        billing,
        shipping: shippingData,
        buttonsContainer
    });
    const {
        createOrder,
        onShippingChange,
        createPaymentToken,
        createVaultSetupToken
    } = useApiRequests({
        isExpress,
        paymentMethod: 'ppcp'
    })
    useEffect(() => {
        currentShippingData.current = shippingData;
        currentBilling.current = billing;
        currentData.current = {
            ...currentData.current,
            shipping: shippingData,
            billing,
            onClick,
            onClose,
            buttonsContainer
        }
        ;
    });

    const getButtonStyle = useCallback(fundingSource => {
        let styles = {};
        switch (fundingSource) {
            case paypal.FUNDING.PAYPAL:
                styles = buttonStyles.paypal;
                break;
            case paypal.FUNDING.PAYLATER:
                styles = buttonStyles.paylater;
                break;
            case paypal.FUNDING.CREDIT:
                const colors = ['black', 'white'];
                const color = colors.includes(buttonStyles.paylater.color) ? buttonStyles.paylater.color : 'darkblue';
                styles = {...buttonStyles.paylater, color};
                break;
            case paypal.FUNDING.CARD:
                styles = buttonStyles.card;
                if (styles.tagline) {
                    delete styles.tagline;
                    styles.layout = 'vertical';
                } else {
                    styles.layout = 'horizontal';
                    delete styles.tagline;
                }
                break;
            case paypal.FUNDING.VENMO:
                styles = buttonStyles.venmo;
                break;
        }
        return styles;
    }, [paypal, buttonStyles]);

    const onApprove = useCallback(async (data, actions) => {
        const paymentData = {
            order: {},
            orderId: data.orderID,
            vaultSetupToken: data.vaultSetupToken || '',
        }
        if (data.vaultSetupToken) {
            createPaymentToken(data.vaultSetupToken).then(response => {
                setPaymentData({...paymentData, paymentToken: response});
            }).finally(() => {
                currentData.current.buttonsContainer.classList.remove('disabled');
            });
        } else {
            actions.order.get().then(response => {
                setPaymentData({...paymentData, order: response});
            }).catch(error => {
                setError(error);
            });
        }
    }, [setError]);

    const onError = useCallback(error => {
        const {
            buttonsContainer
        } = currentData.current;
        if (error?.message?.indexOf('Window is closed') > -1) {
            return;
        }
        if (currentData.current.error) {
            if (currentData.current.error?.code === 'validation_errors') {
                return setError(currentData.current.error.data.errors[0]);
            } else {
                return setError(currentData.current.error.message);
            }
        }
        if (error?.code === 'validation_errors') {
            setError(error.data.errors[0]);
        } else {
            setError(error);
        }
        buttonsContainer.classList.remove('disabled');
    }, [setError]);

    const getOptions = useCallback(fundingSource => {
        const {needsShipping} = currentShippingData.current;
        const options = {
            fundingSource: fundingSource,
            style: getButtonStyle(fundingSource),
            onApprove,
            onError
        };
        if (isExpress) {
            options.onClick = () => currentData.current.onClick();
            options.onCancel = () => currentData.current.onClose()
        } else {
            options.onClick = () => {
                // if address is not valid, show a notification that address data must be filled out first
                if (!isExpress && !currentData.current.buttonState) {
                    if (needsShipping) {
                        setError(i18n.order_missing_address);
                    } else {
                        setError(i18n.order_missing_billing_address);
                    }
                }
            };
            options.onCancel = () => {
                currentData.current.buttonsContainer.classList.remove('disabled');
            }
        }
        options.onInit = (data, actions) => {
            if (!isExpress) {
                currentData.current.actions[fundingSource] = actions;
            }
        }

        if (!needsSetupToken) {
            options.createOrder = (data, actions) => {
                return createOrder(currentData.current);
            };
        } else {
            options.createVaultSetupToken = (...args) => {
                const {
                    buttonsContainer
                } = currentData.current;
                buttonsContainer.classList.add('disabled');
                return createVaultSetupToken(...args, currentData.current);
            }
        }
        if (isExpress && needsShipping && fundingSource !== 'venmo') {
            options.onShippingChange = (...args) => {
                return onShippingChange(...args, currentData.current);
            };
        }

        return options;
    }, [
        paypal,
        isExpress,
        createOrder,
        createVaultSetupToken,
        onApprove,
        onError,
        createOrder,
        onShippingChange,
        needsSetupToken,
        setError,
    ]);

    return {getOptions};
}